import { Box, VStack, HStack, Text, Button, Avatar, Grid, GridItem, Badge, Spinner, useBreakpointValue, useColorModeValue, Tabs, TabList, Tab, TabPanels, TabPanel, IconButton, Image, SimpleGrid, useToast } from '@chakra-ui/react';
import { FiArrowLeft, FiHeart, FiShare } from 'react-icons/fi';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import IconWrapper from '../components/IconWrapper';
import { getPublisherProfile, PublisherProfileData } from '../services/publisherService';
import { ContentItem } from '../services/contentService';
import { 
  followPublisher, 
  unfollowPublisher, 
  isFollowingPublisher,
  likeContent,
  unlikeContent,
  shareContent,
  hasUserLikedContent,
  viewContent
} from '../services/subscriptionService';
import { useAuth } from '../contexts/AuthContext';

const CreatorProfile: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const [publisherData, setPublisherData] = useState<PublisherProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [likedContent, setLikedContent] = useState<Set<string>>(new Set());

  const bgColor = useColorModeValue('white', 'gray.900');
  const textColor = useColorModeValue('gray.800', 'white');
  const lightTextColor = useColorModeValue('gray.600', 'gray.400');
  const statsBgColor = useColorModeValue('gray.50', 'gray.800');

  useEffect(() => {
    const fetchPublisherData = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const data = await getPublisherProfile(id);
        setPublisherData(data);
        
        // Check if current user is following this publisher
        if (user && id) {
          const following = await isFollowingPublisher(id, user.uid);
          setIsSubscribed(following);
        }
        
        // Load liked content for current user
        if (user && data.content) {
          const allContent = [
            ...data.content.series,
            ...data.content.movies,
            ...data.content.shorts,
            ...data.content.books,
            ...data.content.comics,
            ...data.content.anime,
            ...data.content.novels
          ];
          
          const likedContentSet = new Set<string>();
          for (const content of allContent) {
            const isLiked = await hasUserLikedContent(content.id, user.uid);
            if (isLiked) {
              likedContentSet.add(content.id);
            }
          }
          setLikedContent(likedContentSet);
        }
      } catch (error) {
        console.error('Error fetching publisher data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load publisher profile',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPublisherData();
  }, [id, user, toast]);

  const handleFollow = async () => {
    if (!user || !id) {
      toast({
        title: 'Please log in',
        description: 'You need to be logged in to follow',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubscribing(true);
      
      if (isSubscribed) {
        await unfollowPublisher(id, user.uid);
        setIsSubscribed(false);
        toast({
          title: 'Unfollowed',
          description: 'You have unfollowed this creator',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await followPublisher(id, user.uid);
        setIsSubscribed(true);
        toast({
          title: 'Following!',
          description: 'You are now following this creator',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      
      // Refresh publisher data to get updated stats
      const data = await getPublisherProfile(id);
      setPublisherData(data);
    } catch (error) {
      console.error('Error toggling follow:', error);
      toast({
        title: 'Error',
        description: 'Failed to update follow status',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleDonate = () => {
    if (publisherData?.user?.uid) {
      navigate(`/donation/${publisherData.user.uid}`, {
        state: { creatorName: publisherData.user.name || publisherData.user.username || 'Unknown Creator' }
      });
    }
  };

  const handleLikeContent = async (contentId: string) => {
    if (!user) {
      toast({
        title: 'Please log in',
        description: 'You need to be logged in to like content',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const isLiked = likedContent.has(contentId);
      
      if (isLiked) {
        await unlikeContent(contentId, user.uid);
        setLikedContent(prev => {
          const newSet = new Set(prev);
          newSet.delete(contentId);
          return newSet;
        });
      } else {
        await likeContent(contentId, user.uid);
        setLikedContent(prev => new Set(prev).add(contentId));
      }
      
      // Refresh publisher data to get updated stats
      if (id) {
        const data = await getPublisherProfile(id);
        setPublisherData(data);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast({
        title: 'Error',
        description: 'Failed to update like',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleShareContent = async (contentId: string) => {
    if (!user) {
      toast({
        title: 'Please log in',
        description: 'You need to be logged in to share content',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await shareContent(contentId, user.uid);
      
      // Refresh publisher data to get updated stats
      if (id) {
        const data = await getPublisherProfile(id);
        setPublisherData(data);
      }
      
      toast({
        title: 'Shared!',
        description: 'Content shared successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error sharing content:', error);
      toast({
        title: 'Error',
        description: 'Failed to share content',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleViewContent = async (contentId: string) => {
    if (user) {
      try {
        await viewContent(contentId, user.uid);
      } catch (error) {
        console.error('Error recording view:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <Spinner size="xl" color="purple.400" />
      </Box>
    );
  }

  if (!publisherData || !publisherData.user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <Text color={textColor}>Publisher not found</Text>
      </Box>
    );
  }

  const { user: publisherUser, stats, content } = publisherData;

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'k';
    return num.toString();
  };

  return (
    <Box bg={bgColor} minH="100vh">
      {/* Top Bar */}
      <HStack justify="space-between" align="center" p={4} borderBottom="1px" borderColor="gray.200">
        <IconButton
          aria-label="Back"
          icon={<IconWrapper icon={FiArrowLeft} size={20} />}
          variant="ghost"
          onClick={() => navigate(-1)}
        />
        <Text fontSize="lg" fontWeight="bold" color={textColor}>
          Profile
        </Text>
        <Box w="40px" /> {/* Spacer for centering */}
      </HStack>

      {/* Profile Header */}
      <VStack gap={6} p={6} align="center">
        {/* Profile Picture */}
        <Avatar
          size="2xl"
          src={publisherUser?.avatarUrl}
          name={publisherUser?.name || publisherUser?.username || 'User'}
          bg="purple.500"
        />

        {/* Name and Email */}
        <VStack gap={2} align="center">
          <Text fontSize="2xl" fontWeight="bold" color={textColor}>
            {publisherUser?.name || publisherUser?.username || 'Unknown User'}
                  </Text>
          <Text fontSize="sm" color={lightTextColor}>
            {publisherUser?.email || 'No email provided'}
                </Text>
              </VStack>

        {/* Bio */}
        <Text
          fontSize="sm"
          color={lightTextColor}
          textAlign="center"
          maxW="400px"
          lineHeight="1.5"
        >
          {publisherUser?.bio || 'No bio available'}
        </Text>

        {/* Stats */}
        <Box bg={statsBgColor} p={6} borderRadius="lg" w="100%" maxW="400px">
          <SimpleGrid columns={4} gap={4}>
            <VStack gap={1}>
              <Text fontSize="xs" color={lightTextColor} textAlign="center">
                followers
              </Text>
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                {formatNumber(stats.subscribers)}
              </Text>
            </VStack>
                  <VStack gap={1}>
              <Text fontSize="xs" color={lightTextColor} textAlign="center">
                Views
              </Text>
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                {formatNumber(stats.views)}
                    </Text>
                  </VStack>
                  <VStack gap={1}>
              <Text fontSize="xs" color={lightTextColor} textAlign="center">
                Likes
              </Text>
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                {formatNumber(stats.likes)}
                    </Text>
                  </VStack>
                  <VStack gap={1}>
              <Text fontSize="xs" color={lightTextColor} textAlign="center">
                Shares
              </Text>
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                {formatNumber(stats.shares)}
                    </Text>
                  </VStack>
          </SimpleGrid>
        </Box>

        {/* Action Buttons */}
        <HStack gap={4} w="100%" maxW="400px">
                <Button
            flex="1"
            colorScheme="purple"
            size="md"
                  onClick={handleFollow}
            isLoading={isSubscribing}
            loadingText={isSubscribed ? 'Unfollowing...' : 'Following...'}
            disabled={!user}
                >
                  {isSubscribed ? 'Following' : 'Follow'}
                </Button>
          <Button
            flex="1"
            variant="outline"
            colorScheme="purple"
            size="md"
            onClick={handleDonate}
          >
            Donate
          </Button>
        </HStack>
      </VStack>

      {/* Content Tabs */}
      <Box px={{ base: 4, md: 8 }}>
        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="purple">
          <TabList overflowX="auto" scrollBehavior="smooth">
            <Tab fontSize="sm" fontWeight="medium" minW="fit-content" px={4}>Series</Tab>
            <Tab fontSize="sm" fontWeight="medium" minW="fit-content" px={4}>Movies</Tab>
            <Tab fontSize="sm" fontWeight="medium" minW="fit-content" px={4}>Shorts</Tab>
            <Tab fontSize="sm" fontWeight="medium" minW="fit-content" px={4}>Books</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={{ base: 2, md: 4 }} py={6}>
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={{ base: 3, md: 4 }}>
                  {content.series.map((item) => (
                    <Box
                      key={item.id}
                      borderRadius="md"
                      overflow="hidden"
                      cursor="pointer"
                      _hover={{ transform: 'scale(1.02)', transition: 'transform 0.2s' }}
                      onClick={() => {
                        handleViewContent(item.id);
                        navigate(`/short/${item.id}`);
                      }}
                      aspectRatio="1"
                      w="100%"
                      position="relative"
                    >
                      <Image
                        src={item.thumbnail || item.poster || '/placeholder.jpg'}
                        alt={item.title}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                      />
                      <Box
                        position="absolute"
                        top={2}
                        right={2}
                        display="flex"
                        gap={1}
                        opacity={0}
                        _hover={{ opacity: 1 }}
                        transition="opacity 0.2s"
                      >
                        <IconButton
                          aria-label="Like"
                          icon={<IconWrapper icon={FiHeart} size={14} />}
                          size="xs"
                          colorScheme={likedContent.has(item.id) ? "red" : "gray"}
                          variant="solid"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLikeContent(item.id);
                          }}
                        />
                        <IconButton
                          aria-label="Share"
                          icon={<IconWrapper icon={FiShare} size={14} />}
                          size="xs"
                          colorScheme="blue"
                          variant="solid"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShareContent(item.id);
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                {content.series.length === 0 && (
                  <Text color={lightTextColor} textAlign="center" gridColumn="1 / -1">
                    No series content available
                  </Text>
                )}
              </SimpleGrid>
            </TabPanel>
            <TabPanel px={{ base: 2, md: 4 }} py={6}>
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={{ base: 3, md: 4 }}>
                {content.movies.map((item) => (
                  <Box
                    key={item.id}
                    borderRadius="md"
                    overflow="hidden"
                    cursor="pointer"
                    _hover={{ transform: 'scale(1.02)', transition: 'transform 0.2s' }}
                    onClick={() => {
                      handleViewContent(item.id);
                      navigate(`/short/${item.id}`);
                    }}
                    aspectRatio="1"
                    w="100%"
                    position="relative"
                  >
                    <Image
                      src={item.thumbnail || item.poster || '/placeholder.jpg'}
                      alt={item.title}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                    <Box
                      position="absolute"
                      top={2}
                      right={2}
                      display="flex"
                      gap={1}
                      opacity={0}
                      _groupHover={{ opacity: 1 }}
                      transition="opacity 0.2s"
                    >
                      <IconButton
                        aria-label="Like"
                        icon={<IconWrapper icon={FiHeart} size={14} />}
                        size="xs"
                        colorScheme={likedContent.has(item.id) ? "red" : "gray"}
                        variant="solid"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeContent(item.id);
                        }}
                      />
                      <IconButton
                        aria-label="Share"
                        icon={<IconWrapper icon={FiShare} size={14} />}
                        size="xs"
                        colorScheme="blue"
                        variant="solid"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareContent(item.id);
                        }}
                      />
                    </Box>
                  </Box>
                ))}
                {content.movies.length === 0 && (
                  <Text color={lightTextColor} textAlign="center" gridColumn="1 / -1">
                    No movies available
                  </Text>
                )}
              </SimpleGrid>
            </TabPanel>
            <TabPanel px={{ base: 2, md: 4 }} py={6}>
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={{ base: 3, md: 4 }}>
                {content.shorts.map((item) => (
                  <Box
                    key={item.id}
                    borderRadius="md"
                    overflow="hidden"
                    cursor="pointer"
                    _hover={{ transform: 'scale(1.02)', transition: 'transform 0.2s' }}
                    onClick={() => {
                      handleViewContent(item.id);
                      navigate(`/short/${item.id}`);
                    }}
                    aspectRatio="1"
                    w="100%"
                    position="relative"
                  >
                    <Image
                      src={item.thumbnail || item.poster || '/placeholder.jpg'}
                      alt={item.title}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                    <Box
                      position="absolute"
                      top={2}
                      right={2}
                      display="flex"
                      gap={1}
                      opacity={0}
                      _groupHover={{ opacity: 1 }}
                      transition="opacity 0.2s"
                    >
                      <IconButton
                        aria-label="Like"
                        icon={<IconWrapper icon={FiHeart} size={14} />}
                        size="xs"
                        colorScheme={likedContent.has(item.id) ? "red" : "gray"}
                        variant="solid"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeContent(item.id);
                        }}
                      />
                      <IconButton
                        aria-label="Share"
                        icon={<IconWrapper icon={FiShare} size={14} />}
                        size="xs"
                        colorScheme="blue"
                        variant="solid"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareContent(item.id);
                        }}
                      />
                    </Box>
                  </Box>
                ))}
                {content.shorts.length === 0 && (
                  <Text color={lightTextColor} textAlign="center" gridColumn="1 / -1">
                    No shorts available
                  </Text>
                )}
              </SimpleGrid>
            </TabPanel>
            <TabPanel px={{ base: 2, md: 4 }} py={6}>
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={{ base: 3, md: 4 }}>
                {[...content.books, ...content.comics].map((item) => (
                  <Box
                    key={item.id}
                    borderRadius="md"
                    overflow="hidden"
                    cursor="pointer"
                    _hover={{ transform: 'scale(1.02)', transition: 'transform 0.2s' }}
                    onClick={() => {
                      handleViewContent(item.id);
                      navigate(`/book/${item.id}`);
                    }}
                    aspectRatio="1"
                    w="100%"
                    position="relative"
                  >
                    <Image
                      src={item.thumbnail || item.poster || '/placeholder.jpg'}
                      alt={item.title}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                    <Box
                      position="absolute"
                      top={2}
                      right={2}
                      display="flex"
                      gap={1}
                      opacity={0}
                      _groupHover={{ opacity: 1 }}
                      transition="opacity 0.2s"
                    >
                      <IconButton
                        aria-label="Like"
                        icon={<IconWrapper icon={FiHeart} size={14} />}
                        size="xs"
                        colorScheme={likedContent.has(item.id) ? "red" : "gray"}
                        variant="solid"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeContent(item.id);
                        }}
                      />
                      <IconButton
                        aria-label="Share"
                        icon={<IconWrapper icon={FiShare} size={14} />}
                        size="xs"
                        colorScheme="blue"
                        variant="solid"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareContent(item.id);
                        }}
                      />
                    </Box>
                  </Box>
                ))}
                {content.books.length === 0 && content.comics.length === 0 && (
                  <Text color={lightTextColor} textAlign="center" gridColumn="1 / -1">
                    No books or comics available
                  </Text>
                )}
              </SimpleGrid>
            </TabPanel>
          </TabPanels>
        </Tabs>

      </Box>
    </Box>
  );
};

export default CreatorProfile;
